import type { TimePeriod } from '../components/TimePeriodSelector';
import { londonLastMonthRange, londonMonthToDateRange } from './ordersDailyPresentation';
import { ukTodayYmd } from './ukTime';

/**
 * The Maintenance tab's window for a period chip. Its preset chips are the
 * stored Maintenance tab periods in zeecore (maintenanceTabStoredView.ts
 * `maintenanceTabWindows`), which computes the same windows to the
 * millisecond — a request for one is served from storage. Change both
 * together.
 *
 * Not the Driver tab's windows (controlCentrePeriodRange.ts): here "last 7
 * days" is today and the 6 days before, and "last 30/180 days" today and the
 * 29/179 before. "Today" is the UK date, each day written with UTC-midnight
 * bounds (the UK day for Aquarius rows, stored as UK clock labelled UTC);
 * month chips use the London month.
 */
export function maintenancePeriodRange(period: TimePeriod, now: Date = new Date()): { startDate: Date; endDate: Date } {
  const todayStr = ukTodayYmd(now);
  const daysBack = (days: number) => {
    const d = new Date(`${todayStr}T12:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() - days);
    return d.toISOString().slice(0, 10);
  };
  const day = (start: string, end: string) => ({
    startDate: new Date(`${start}T00:00:00.000Z`),
    endDate: new Date(`${end}T23:59:59.999Z`),
  });

  switch (period) {
    case 'today':
      return day(todayStr, todayStr);
    case 'yesterday':
      return day(daysBack(1), daysBack(1));
    case 'last-week':
      return day(daysBack(6), todayStr);
    case 'month-to-date': {
      const { start, end } = londonMonthToDateRange(now);
      return day(start, end);
    }
    case 'last-month': {
      const { start, end } = londonLastMonthRange(now);
      return day(start, end);
    }
    case 'last_30_days':
      return day(daysBack(29), todayStr);
    case 'last_180_days':
      return day(daysBack(179), todayStr);
    case 'all-time':
      return { startDate: new Date('2020-01-01T00:00:00.000Z'), endDate: new Date(`${todayStr}T23:59:59.999Z`) };
    case 'custom':
    default:
      // Chip bar has no custom picker — treat unknown/custom like Today.
      return day(todayStr, todayStr);
  }
}
